/**
 * Kaching Bundles — Product Image + Quantity Badge
 * Regenbogenkreis · Shopify
 *
 * What this does:
 * → Injects one product image per bundle card
 * → Overlays an ×N badge (brand green) on the bottom-right of the image for qty > 1
 * → Hides the Kaching-generated bundle name text (Single / Duo / Trio etc.)
 *
 * HOW TO INSTALL:
 * 1. Shopify Admin → Online Store → Themes → Edit code
 * 2. Assets → kaching-bundle-images.js → paste this file
 * 3. layout/theme.liquid → add before </body>:
 *      {{ 'kaching-bundle-images.js' | asset_url | script_tag }}
 */

(function () {
  'use strict';

  // ─── LOAD GOOGLE FONT ──────────────────────────────────────────────────────

  if (!document.querySelector('#kb-google-font')) {
    const link = document.createElement('link');
    link.id = 'kb-google-font';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Yanone+Kaffeesatz:wght@700&display=swap';
    document.head.appendChild(link);
  }

  // ─── HIDE BUNDLE TITLE LABELS ──────────────────────────────────────────────

  if (!document.querySelector('#kb-custom-styles')) {
    const style = document.createElement('style');
    style.id = 'kb-custom-styles';
    style.textContent = `
      .kaching-bundles__bar-title { display: none !important; }
      @media (max-width: 768px) {
        .kb-qty-badge {
          width: 36px !important;
          height: 36px !important;
          right: -6px !important;
        }
        .kb-qty-badge span:first-child {
          font-size: 22px !important;
        }
        .kb-qty-badge span:last-child {
          font-size: 30px !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // ─── CONFIG ────────────────────────────────────────────────────────────────

  const SELECTORS = {
    widget:      '.kaching-bundles__bars',
    option:      '.kaching-bundles__bar',
    imageTarget: '.kaching-bundles__bar-main',
    quantityAttr: 'data-quantity',
    title:       '.kaching-bundles__bar-title',
  };

  const IMAGE = {
    size: 90, // px — product image width & height
  };

  const BADGE = {
    size:       48,                                    // px — circle diameter
    bgColor:    '#9D2F92',                             // brand green
    textColor:  '#ffffff',
    fontFamily: "'Yanone Kaffeesatz', sans-serif",
    xSize:      '32px',                               // size of the "x" letter
    numSize:    '40px',                               // size of the number
  };

  // ─── PRODUCT IMAGE URL ─────────────────────────────────────────────────────

  function getProductImageUrl() {
    try {
      const meta = window.ShopifyAnalytics?.meta;
      if (meta?.product?.variants?.[0]?.image) return meta.product.variants[0].image;
    } catch (_) {}

    try {
      if (window.product?.featured_image) return window.product.featured_image;
    } catch (_) {}

    try {
      const og = document.querySelector('meta[property="og:image"]');
      if (og) return og.getAttribute('content');
    } catch (_) {}

    try {
      const img = document.querySelector(
        '.product__media img, .product-single__photo img, [data-product-featured-image]'
      );
      if (img) return img.src || img.dataset.src;
    } catch (_) {}

    return null;
  }

  // ─── QUANTITY PARSER ───────────────────────────────────────────────────────

  function parseQuantity(el, indexFallback) {
    // 0. Title element read directly via textContent — this works even though
    //    the title is hidden with display:none via CSS above. innerText would
    //    return an empty string for hidden elements (it respects rendered
    //    layout/visibility), which was the root cause of the badge always
    //    falling back to the card position instead of the real quantity.
    const titleEl = el.querySelector(SELECTORS.title);
    const titleText = titleEl ? (titleEl.textContent || '') : '';

    // 1. Data attribute
    const fromAttr = el.getAttribute(SELECTORS.quantityAttr)
      || el.dataset.quantity
      || el.dataset.items
      || el.dataset.count;
    if (fromAttr && !isNaN(parseInt(fromAttr))) return parseInt(fromAttr);

    // 2. Digit patterns — check the (possibly hidden) title text first,
    //    then fall back to whatever visible text innerText picks up.
    const text = titleText || el.innerText || el.textContent || '';
    const digitMatch = text.match(/\b([1-9]\d?)\s*[x×✕]/i)
      || text.match(/\b([1-9]\d?)\s*er\b/i)
      || text.match(/\b([1-9]\d?)\s*pack/i)
      || text.match(/\b([1-9]\d?)\s*stück/i)
      || text.match(/^\s*([1-9]\d?)\s*$/m);
    if (digitMatch) return parseInt(digitMatch[1]);

    // 3. German word mapping
    const words = { einzel: 1, single: 1, zwei: 2, double: 2, duo: 2, drei: 3, triple: 3, trio: 3, vier: 4, fünf: 5, sechs: 6 };
    const lower = text.toLowerCase();
    for (const [word, qty] of Object.entries(words)) {
      if (lower.includes(word)) return qty;
    }

    // 4. Fallback: card position (0 → 1, 1 → 2, 2 → 3 ...)
    //    Only reached if the title element is missing entirely or contains
    //    no recognizable quantity — should be rare now that step 0 reads it.
    if (typeof indexFallback === 'number') return indexFallback + 1;

    return 1;
  }

  // ─── BUILD IMAGE + BADGE ───────────────────────────────────────────────────

  function buildWidget(imageUrl, count) {
    const wrapper = document.createElement('div');
    wrapper.className = 'kb-img-badge-wrap';
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.style.cssText = `
      position: relative;
      display: inline-block;
      margin: 0 auto 8px;
      flex-shrink: 0;
    `;

    // Product image
    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = '';
    img.loading = 'lazy';
    img.style.cssText = `
      display: block;
      width: ${IMAGE.size}px;
      height: ${IMAGE.size}px;
      object-fit: contain;
    `;
    wrapper.appendChild(img);

    // Badge — only for qty > 0
    if (count > 0) {
      const badge = document.createElement('div');
      badge.className = 'kb-qty-badge';
      badge.style.cssText = `
        position: absolute;
        bottom: 0;
        right: -10px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: ${BADGE.size}px;
        height: ${BADGE.size}px;
        border-radius: 50%;
        background: ${BADGE.bgColor};
        color: ${BADGE.textColor};
        font-family: ${BADGE.fontFamily};
        font-weight: 700;
        line-height: 1;
        box-shadow: 0 2px 6px rgba(0,0,0,0.18);
      `;

      const inner = document.createElement('div');
      inner.style.cssText = `display: flex; align-items: flex-end;`;

      const xSpan = document.createElement('span');
      xSpan.textContent = 'x\u00a0';
      xSpan.style.cssText = `font-size: ${BADGE.xSize}; padding-bottom: 2px;`;

      const numSpan = document.createElement('span');
      numSpan.textContent = count;
      numSpan.style.cssText = `font-size: ${BADGE.numSize};`;

      inner.appendChild(xSpan);
      inner.appendChild(numSpan);
      badge.appendChild(inner);
      wrapper.appendChild(badge);
    }

    return wrapper;
  }

  // ─── INJECT INTO CARDS ─────────────────────────────────────────────────────

  function inject() {
    const imageUrl = getProductImageUrl();
    if (!imageUrl) {
      console.warn('[KachingBundleImages] Product image URL not found.');
      return;
    }

    let options = Array.from(document.querySelectorAll(SELECTORS.option));

    if (options.length === 0) {
      const widget = document.querySelector(SELECTORS.widget);
      if (widget && widget.children.length >= 2) {
        options = Array.from(widget.children);
      }
    }

    if (options.length === 0) {
      console.warn('[KachingBundleImages] No bundle option cards found. Check SELECTORS.option.');
      return;
    }

    options.forEach((optionEl, index) => {
      if (optionEl.dataset.kbInjected) return;
      optionEl.dataset.kbInjected = 'true';

      const quantity = parseQuantity(optionEl, index);
      const widget = buildWidget(imageUrl, quantity);

      const target = optionEl.querySelector(SELECTORS.imageTarget) || optionEl;
      target.insertBefore(widget, target.firstChild);
    });

    console.log(`[KachingBundleImages] ✓ Done — ${options.length} cards updated.`);
  }

  // ─── INIT ──────────────────────────────────────────────────────────────────

  function init() {
    let done = false;

    function tryInject() {
      const widget = document.querySelector(SELECTORS.widget);
      const hasCards = widget && (
        widget.querySelectorAll(SELECTORS.option).length > 0 ||
        widget.children.length >= 2
      );
      if (widget && hasCards && !done) {
        done = true;
        inject();
        observer.disconnect();
        clearInterval(poll);
      }
    }

    const observer = new MutationObserver(tryInject);
    observer.observe(document.body, { childList: true, subtree: true });

    let attempts = 0;
    const poll = setInterval(() => {
      tryInject();
      if (++attempts > 40) {
        clearInterval(poll);
        observer.disconnect();
        if (!done) console.warn('[KachingBundleImages] Widget not found after 20s.');
      }
    }, 500);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', tryInject);
    } else {
      tryInject();
    }
  }

  init();

})();